/** Deterministic xmur3 seed expansion followed by mulberry32 output. */
export class SeededRandom {
    state;
    constructor(seed) {
        this.state = seedWord(seed);
    }
    next() {
        this.state = (this.state + 0x6d2b79f5) >>> 0;
        let t = this.state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }
    int(minInclusive, maxInclusive) {
        if (maxInclusive <= minInclusive)
            return minInclusive;
        return minInclusive + Math.floor(this.next() * (maxInclusive - minInclusive + 1));
    }
    pick(values) {
        if (values.length === 0)
            throw new Error('cannot pick from an empty array');
        return values[this.int(0, values.length - 1)];
    }
    weightedPick(values, weightOf) {
        if (values.length === 0)
            throw new Error('cannot pick from an empty array');
        const weights = values.map((value) => Math.max(0, weightOf(value)));
        const total = weights.reduce((sum, value) => sum + value, 0);
        if (!(total > 0))
            return this.pick(values);
        let cursor = this.next() * total;
        for (let index = 0; index < values.length; index += 1) {
            cursor -= weights[index] ?? 0;
            if (cursor <= 0)
                return values[index];
        }
        return values[values.length - 1];
    }
    shuffle(values) {
        const out = [...values];
        for (let index = out.length - 1; index > 0; index -= 1) {
            const other = this.int(0, index);
            [out[index], out[other]] = [out[other], out[index]];
        }
        return out;
    }
}
export function normaliseSeed(seed) {
    if (typeof seed === 'number') {
        if (!Number.isFinite(seed))
            return '0';
        return String(seed);
    }
    return seed.length > 0 ? seed : 'empty-seed';
}
function seedWord(input) {
    let hash = 1779033703 ^ input.length;
    for (let index = 0; index < input.length; index += 1) {
        hash = Math.imul(hash ^ input.charCodeAt(index), 3432918353);
        hash = (hash << 13) | (hash >>> 19);
    }
    hash = Math.imul(hash ^ (hash >>> 16), 2246822507);
    hash = Math.imul(hash ^ (hash >>> 13), 3266489909);
    return (hash ^= hash >>> 16) >>> 0;
}
function canonicalise(value) {
    if (Array.isArray(value))
        return value.map(canonicalise);
    if (value && typeof value === 'object') {
        const record = value;
        const out = {};
        for (const key of Object.keys(record).sort()) {
            const child = record[key];
            if (child !== undefined)
                out[key] = canonicalise(child);
        }
        return out;
    }
    return value;
}
export function canonicalJson(value) {
    return JSON.stringify(canonicalise(value));
}
/** Stable 64-bit FNV-1a fingerprint over canonical JSON. */
export function stableFingerprint(value) {
    const text = canonicalJson(value);
    let hash = 0xcbf29ce484222325n;
    const prime = 0x100000001b3n;
    const mask = 0xffffffffffffffffn;
    for (let index = 0; index < text.length; index += 1) {
        hash ^= BigInt(text.charCodeAt(index));
        hash = (hash * prime) & mask;
    }
    return hash.toString(16).padStart(16, '0');
}
//# sourceMappingURL=random.js.map